import { useCallback, useRef, useState } from 'react';

import { FocusContext } from '../contexts/FocusContext.js';
import { useFocusContext } from '../hooks/useFocusContext.js';
import type { WithChildren } from '../types/with-children.js';
import { getProperInertValue } from '../utils/get-inert-value.js';
import { mergeProps } from '../utils/merge-props.js';

/**
 * Provider that can be reused in components for storing the focus context props.
 * Loosely inspired by [what-input-js](https://github.com/ten1seven/what-input/blob/main/src/scripts/what-input.js).
 * @internal
 */
export const FocusProvider = ({ children }: WithChildren) => {
  const [backgroundInertness, setBackgroundInertness] = useState(false);
  const focusContext = useFocusContext();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const focusTriggerElement = useCallback((ref: HTMLElement | undefined) => {
    if (wrapperRef.current?.getAttribute('inert') === null) {
      ref?.focus();
      return;
    }

    // We want to focus the trigger element after the inert prop got removed.
    // Otherwise, we sometimes lose the focus on slow computers.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'inert'
        ) {
          if (wrapperRef.current?.getAttribute('inert') === null) {
            ref?.focus();
            observer.disconnect();
            break;
          }
        }
      }
    });

    if (wrapperRef.current !== null) {
      observer.observe(wrapperRef.current, { attributes: true });

      setTimeout(() => {
        observer.disconnect();
      }, 5000);
    }
  }, []);

  const focusProps = backgroundInertness
    ? {
        inert: getProperInertValue(true),
        tabIndex: -1,
        'aria-hidden': true,
      }
    : { inert: undefined, tabIndex: undefined, 'aria-hidden': false };

  return (
    <FocusContext.Provider
      value={{
        ...mergeProps(focusContext, {
          backgroundInertness,
          setBackgroundInertness,
          focusTriggerElement,
        }),
      }}
    >
      <div ref={wrapperRef} {...focusProps}>
        {children}
      </div>
    </FocusContext.Provider>
  );
};

FocusProvider['displayName'] = 'FocusProvider';
